import React, { useMemo } from "react";
import { useDataWithRoute } from "../utils/hooks";
import { VsiBubble } from "../styled/App.styled";
import ThreeDeeBubbleChart from "../components/3DBubbleChart";
import { snakeCaseToTitleCase } from "../utils/Helpers";
import { Spinner } from "reactstrap";
import { useVsiAppContext } from "../stores/VsiApp";

export default function FileByLastUsed() {
  const { client: { clientId }, getPeriodId } = useVsiAppContext();
  const periodId = getPeriodId("FILES");

  const route = useMemo(() => `/api/v6/report/file_analysis_detail_proc?client_id=${clientId}&period_id=${periodId}`, [clientId, periodId]);
  const [{ recordset }, { isLoading }] = useDataWithRoute(route);

  const { bubbleData, categories } = useMemo(() => {
    const updatedData = [];
    const categories = [];

    const defaultBucket = {
      text_size: 0,
      text_count: 0,
      av_size: 0,
      av_count: 0,
      db_size: 0,
      db_count: 0,
      compress_size: 0,
      compress_count: 0,
      custom_size: 0,
      custom_count: 0,
      other_size: 0,
      other_count: 0,
      total_size: 0,
      total_count: 0
    };

    const allTypes = {
      "db": [],
      "text": [],
      "av": [],
      "compress": [],
      "other": []
    };

    let buckets = {
      "< 30 Days": { ...defaultBucket},
      "1 - 3 Months": { ...defaultBucket},
      "3 - 6 Months": { ...defaultBucket},
      "6 - 12 Months": { ...defaultBucket},
      "1 - 2 Years": { ...defaultBucket},
      "Over 2 Years": { ...defaultBucket}
    };

    let rangeMapping = {
      // KEY: field value of the bucket we possibly get from proc
      // VALUE: which bucket it should map to
      "Over 1 Year": "1 - 2 Years",
      30: "< 30 Days",
      90: "1 - 3 Months",
      180: "3 - 6 Months",
      365: "6 - 12 Months",
      730: "1 - 2 Years",
      older: "Over 2 Years"
    };

    for (let i = 0; i < recordset.length; i++) {
      const datum = recordset[i];

      let ableToMap = Object.keys(rangeMapping).includes(datum.bucket);
      if (datum.bucket in buckets || ableToMap) {
        let bucketRange = ableToMap ? rangeMapping[datum.bucket] : datum.bucket;
        let chosenBucket = buckets[bucketRange];
        let result = {};

        for (const key of Object.keys(chosenBucket)) {
          result[key] = (chosenBucket[key] || 0) + (datum[key] || 0);
        }
        buckets[bucketRange] = result;
      }
    }

    for (let i = 0; i < Object.keys(buckets).length; i++) {
      buckets[Object.keys(buckets)[i]] = {
        ...buckets[Object.keys(buckets)[i]],
        bucket: Object.keys(buckets)[i]
      };
    }

    const bucketData = [
      buckets["< 30 Days"],
      buckets["1 - 3 Months"],
      buckets["3 - 6 Months"],
      buckets["6 - 12 Months"],
      buckets["1 - 2 Years"],
      buckets["Over 2 Years"]
    ];

    for (let i = 0; i < bucketData.length; i++) {
      const datum = bucketData[i];
      categories.push(datum.bucket);
      for (let key in allTypes) {
        allTypes[key].push([
          datum.bucket,
          parseInt(datum[`${key}_count`]),
          parseFloat(datum[`${key}_size`].toFixed(2))
        ]);
      }
    }

    for (let key in allTypes) {
      updatedData.push({
        name: snakeCaseToTitleCase(key),
        data: allTypes[key],
        tooltip: {
          pointFormat:
            "{series.name} Size: <b>{point.z}</b><br/>{series.name} Count: <b>{point.y}</b>"
        }
      });
    }

    return {
      bubbleData: updatedData,
      categories
    };
  }, [recordset]);

  return isLoading ? (
    <Spinner color={"success"} />
  ) : (
    <VsiBubble>
      <ThreeDeeBubbleChart height={400}
                           series={bubbleData}
                           categories={categories}
                           includeLegend={true}
                           title={"File Size/Count by Last Accessed"}
                           config={{
                             yAxis: {
                               title: {
                                 text: "Count"
                               }
                             }
                           }} />
    </VsiBubble>
  );
}
